import { useEffect, useRef, useState } from "react";
import SignatureCanvas from 'react-signature-canvas';
import { StatusEnum } from "../../../../common/data/base/enums/statusEnum";
import MESSAGES from "../../../../common/data/constants/messages";
import { IEmployee } from "../../../../common/data/dtos/employee";
import { IWorkResourceEmployee } from "../../../../common/data/dtos/work-resource-employee";
import WorkResourceEnum from "../../../../common/data/enums/enumWorkResouce";
import WorkResourceEmployeeStatusEnum from "../../../../common/data/enums/enumWorkResourceEmployeeStatus";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardFooterRight, CardTabItem } from "../../../../components/bootstrap/Card";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../components/bootstrap/Modal";
import Spinner from "../../../../components/bootstrap/Spinner";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import workResourceEmployeeService from "../../../../services/work-resource-employee-service";

interface Props {
    isModalOpened: boolean;
    selectedEmployee: IEmployee | null;
    signItemType: WorkResourceEnum | undefined;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const WorkResourceSignatureModal = ({ isModalOpened, selectedEmployee, signItemType, onModalClose, onAfterSubmit }: Props) => {

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [notDeliveredWorkResourcesEmployee, setNotDeliveredWorkResourcesEmployee] = useState<IWorkResourceEmployee[]>([]);

    const sigCanvas = useRef<SignatureCanvas | null>(null);

    useEffect(() => {
        if (selectedEmployee?.id) {
            resetSignature();
            fetchWorkResourcesEmployee();
        }
    }, []);

    const fetchWorkResourcesEmployee = async () => {
        setIsSubmitting(true);

        if (!selectedEmployee?.id) {
            showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            setIsSubmitting(false);
        } else {

            const response = await workResourceEmployeeService.getWorkResourceEmployeeByEmployee(selectedEmployee.id);

            setIsSubmitting(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            if (response.workResourceEmployees) {
                const notDelivered = response.workResourceEmployees.filter(item => item.situation === WorkResourceEmployeeStatusEnum.notDelivered);

                setNotDeliveredWorkResourcesEmployee(notDelivered || [])
            } else {
                setNotDeliveredWorkResourcesEmployee([])
            }
        }
    };

    const onFormSubmit = async () => {
        if (sigCanvas && sigCanvas.current) {

            const dataUrl = sigCanvas.current?.toDataURL();
            if (!dataUrl) {
                showWarning(MESSAGES.SIGN.VALIDATION_ERROR);
                return;
            }

            setIsSubmitting(true);

            const itemsToSign = notDeliveredWorkResourcesEmployee?.filter(
                p => p.workResource?.type === signItemType
            ) ?? [];

            if (itemsToSign.length === 0) {
                showWarning(MESSAGES.SIGN.NOT_FOUND_ITEMS);
                return;
            }

            const response = await workResourceEmployeeService.signWorkResourceEmployee({
                sign: dataUrl,
                workResourcesEmployee: itemsToSign,
                rowVersion: 1,
                status: StatusEnum.Active
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            resetSignature();

            showSuccess(MESSAGES.SIGN.SUCCESS);
            onClose();
        } else {
            showWarning(MESSAGES.SIGN.VALIDATION_ERROR);
        }
    }

    const onClose = () => {
        onModalClose();
    };

    const resetSignature = () => {
        if (sigCanvas && sigCanvas.current)
            sigCanvas.current?.clear();
    }

    const getResponsibilityAgreementText = () => {

        if (signItemType === WorkResourceEnum.tool) {

            return (
                <>
                    <div className='col-lg-12 col-md-12 col-sm-12'>
                        <p className='lead'>
                            Declaro ter recebido as  ferramentas e equipamentos abaixo relacionados, e estou ciente que sou responsável pela sua guarda e conservação e que em caso de danos e extravio, se confirmada minha responsabilidade, autorizo o desconto em meu salário mensal ou na rescisão do meu contrato de trabalho de acordo com o Art. 462, Parágrafo 1 da CLT (Consolidação das Leis Trabalhistas).
                        </p>
                    </div>
                </>
            )

        } else {

            return (
                <>
                    <div className='col-lg-12 col-md-12 col-sm-12'>
                        <p className='lead'>
                            Declaro para os devidos fins, haver recebido gratuitamente os EPI's descritos nesta ficha, bem como recebido treinamento para uso, higienização e conservação destes, o qual comprometo-me a usar sistematicamente em meu trabalho, mediante ainda, os termos seguintes:
                        </p>

                        <ol type="a">
                            <li className='lead'>Os EPI's serão usados unicamente para a finalidade a que se destinam;</li>
                            <li className='lead'>Comunicarei ao empregador (chefia imediata) qualquer alteração no EPI que os torne impróprio para o uso ou o seu extravio;</li>
                            <li className='lead'>Estou ciente e de pleno acordo que a não utilização do equipamento por motivo não justificado constitui ato faltoso e está sujeito às sanções disciplinares legais;</li>
                            <li className='lead'>Estou ciente que é de minha responsabilidade a guarda e a conservação dos EPI's entregue para o meu uso pessoal;</li>
                            <li className='lead'>Estou ciente que devo devolvê-lo quando da rescisão do contrato de trabalho ou quando do período de troca;</li>
                            <li className='lead'>
                                Conforme Art. 462 § 1º - Em caso de dano causado pelo empregado, o desconto será lícito, desde que a possibilidade tenha sido acordada, ou na ocorrência de dolo do empregado, autorizo, expressamente, a empresa a proceder ao desconto em meus vencimentos, gratificações, indenizações, do valor do EPI quando ocorrer uma das situações: danificado propositalmente, extravio culposo, não devolvidos à empresa para substituição, não devolvidos à empresa por ocasião de eventual desligamento.
                            </li>
                        </ol>
                    </div>
                </>
            )
        }
    }

    return (

        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'xl'} isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id='work-resource-signature-modal'>Confirmar recebimento</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor='primary'>
                    <CardTabItem id='tab-item-1' title={selectedEmployee?.name || 'Confirmar recebimento'} icon='AssignmentInd'>

                        <div className='row g-4'>

                            {
                                getResponsibilityAgreementText()
                            }

                            <div className='col-lg-12 col-md-12 col-sm-12 d-flex justify-content-center'>
                                <div
                                    style={{
                                        border: '2px dashed #aaa',
                                        borderRadius: '8px',
                                        padding: '8px',
                                        backgroundColor: '#fdfdfd',
                                    }}
                                >
                                    <SignatureCanvas
                                        penColor="black"
                                        canvasProps={{ width: 500, height: 200, className: 'sigCanvas' }}
                                        ref={sigCanvas}
                                    />
                                </div>
                            </div>

                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className='bg-transparent'>
                <CardFooterRight>

                    <Button color='warning' onClick={resetSignature} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Limpar
                    </Button>

                    <Button color='success' onClick={onFormSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>


                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
}